import Trix from "trix";
import { ReactTrixRTEInput, ReactTrixRTEToolbar } from "react-trix-rte";
import { useController } from "react-hook-form";
import { ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Flex, useDisclosure, useOutsideClick, usePopper } from "@chakra-ui/react";
import EmojiPicker, { SuggestionMode, Theme } from "emoji-picker-react";
import clsx from "clsx";
import { RichText } from "./trixrichtext";
import "./styles.css";

type TrixEditorProps = {
  name: string;
  control: any;
  wrapper?: (editor: ReactElement, hasValue: boolean) => ReactElement;
  onHasValueChange?: (hasValue: boolean) => void;
  trixRef?: React.Ref<Trix>;
  onInitialize?: (event: Event, richText: RichText) => void;
  isEditable?: boolean;
  hideToolbar?: boolean;
  alwaysShowToolbar?: boolean;
  maintainToolbarSpace?: boolean;
  includeAttachment?: boolean;
  /** When true, drop the heading/size button from the toolbar — use for
   *  surfaces that don't support headings (e.g. Slack messages, task descriptions). */
  hideHeading?: boolean;
  /** Render headings at email-client sizes (browser defaults, em-based) so
   *  authors see the scale recipients get — use for email bodies. */
  emailHeadingSizes?: boolean;
  toolbarBgColor?: string;
  editorBgColor?: string;
  [key: string]: any;
};

function TrixEditor({
  name,
  control,
  wrapper,
  onHasValueChange,
  trixRef,
  onInitialize,
  isEditable = true,
  hideToolbar = false,
  alwaysShowToolbar = false,
  maintainToolbarSpace = false,
  includeAttachment = true,
  hideHeading = false,
  emailHeadingSizes = false,
  toolbarBgColor,
  editorBgColor,
  ...rest
}: TrixEditorProps) {
  const emojiPicker = useDisclosure();
  const isEmojiPickerOpenRef = useRef(false);
  const [idSuffix] = useState(crypto.randomUUID());
  const [isFocused, setIsFocused] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const popperBoxRef = useRef<HTMLDivElement | null>(null);
  const trixEditorElementRef = useRef<any>(null);
  const editorId = `trix-editor-${idSuffix}`;
  const toolbarId = `trix-toolbar-${idSuffix}`;

  useEffect(() => {
    isEmojiPickerOpenRef.current = emojiPicker.isOpen;
  }, [emojiPicker.isOpen]);

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    defaultValue: "",
  });

  const { referenceRef, popperRef, getPopperProps } = usePopper({
    placement: "right",
    modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
  });

  // Close the emoji picker on outside click. Chakra's usePopper returns a
  // callback ref, but useOutsideClick needs a ref object with `.current` —
  // so we mirror the popper element into a separate ref for containment checks.
  // The emoji button has its own open handler; skip clicks on it to avoid a
  // close/reopen flicker.
  useOutsideClick({
    ref: popperBoxRef,
    handler: (event) => {
      if (!emojiPicker.isOpen) return;
      const target = event.target as Node | null;
      if (target && emojiButtonRef.current?.contains(target)) return;
      emojiPicker.onClose();
    },
  });

  const hasValue = !!value?.trim();

  useLayoutEffect(() => {
    onHasValueChange?.(hasValue);
  }, [hasValue, onHasValueChange]);

  // Trix v2 doesn't auto-read content from the linked hidden input.
  // Load HTML into the editor once the form value arrives (empty on first render
  // because React Hook Form's `values` prop syncs via useEffect).
  const latestValueRef = useRef(value);
  useEffect(() => {
    latestValueRef.current = value;
    loadValueIntoEmptyEditor();
  }, [value]);

  // The value can also arrive between mount and trix-initialize, when the
  // editor doesn't exist yet — onInitialize calls this again to catch up.
  function loadValueIntoEmptyEditor() {
    const editor = trixEditorElementRef.current?.editor;
    if (!latestValueRef.current || !editor) return;
    if (!editor.getDocument().toString().trim()) {
      editor.loadHTML(latestValueRef.current);
    }
  }

  const handleEditorChange = useCallback(
    (event: Event) => {
      onChange(event);
    },
    [onChange],
  );

  const handleIsEditable = useCallback(
    (updatedState: boolean) => {
      if (isEditable) {
        setIsFocused(updatedState);
      }
    },
    [isEditable, setIsFocused],
  );

  const toolbarActions = [
    "link",
    "bold",
    "italic",
    "heading1",
    "bullet",
    "number",
    "quote",
    "code",
    "outdent",
    "indent",
    "attachFiles",
    "undo",
    "redo",
  ];

  const editorFlex = (
    <Flex
      className={clsx("trix-container", isFocused ? "is-focused" : "not-focused", {
        "is-editable": isEditable,
        "hide-toolbar": hideToolbar,
        "email-heading-sizes": emailHeadingSizes,
      })}
      direction="column-reverse"
      gap={2}
      style={editorBgColor ? ({ "--trix-editor-bg": editorBgColor } as React.CSSProperties) : undefined}
    >
      <div
        ref={toolbarRef}
        style={{
          ...(!isFocused && !alwaysShowToolbar
            ? maintainToolbarSpace
              ? { visibility: "hidden" as const, pointerEvents: "none" as const }
              : { display: "none" as const }
            : undefined),
          ...(toolbarBgColor && ({ "--trix-toolbar-bg": toolbarBgColor } as React.CSSProperties)),
        }}
      >
        <ReactTrixRTEToolbar
          toolbarId={toolbarId}
          toolbarActions={toolbarActions.filter((action) => {
            if (!includeAttachment && action === "attachFiles") return false;
            if (hideHeading && action === "heading1") return false;
            return true;
          })}
        />
      </div>
      <ReactTrixRTEInput
        className={clsx("trix-editor", {
          "has-value": hasValue,
          "is-focused": isFocused,
          "is-editable": isEditable,
          "hide-toolbar": hideToolbar,
        })}
        id={editorId}
        toolbarId={toolbarId}
        key={name}
        name={name}
        defaultValue={value || ""}
        onFocus={() => handleIsEditable(true)}
        onBlur={() => {
          setTimeout(() => {
            const active = document.activeElement;
            // Trix defers trix-blur, so this can run after focus has already
            // returned to the editor (click out → quickly click back in) —
            // hiding the toolbar mid-edit unless we check the editor too.
            if (trixEditorElementRef.current?.contains?.(active)) return;
            if (!toolbarRef.current?.contains(active) && !isEmojiPickerOpenRef.current) {
              handleIsEditable(false);
            }
          }, 0);
        }}
        onChange={(event: Event) => handleEditorChange(event)}
        onInitialize={(event: Event) => {
          trixEditorElementRef.current = event.target;
          loadValueIntoEmptyEditor();

          const richText = new RichText(event.target);
          richText.setup();

          const button = richText.emojiButton as HTMLButtonElement;
          emojiButtonRef.current = button;
          referenceRef(button);

          button.addEventListener("click", (e) => {
            e.preventDefault(); // prevent focus loss
            emojiPicker.onOpen();
          });

          if (onInitialize) {
            onInitialize(event, richText);
          }
        }}
        isRailsDirectUpload
        trixInputRef={trixRef}
        {...rest}
      />
    </Flex>
  );

  return (
    <>
      {wrapper ? wrapper(editorFlex, hasValue) : editorFlex}
      {emojiPicker.isOpen && (
        <Box
          {...getPopperProps()}
          ref={(node) => {
            popperRef(node);
            popperBoxRef.current = node;
          }}
          className="floating-emoji-picker"
          style={{ zIndex: 2000 }}
          width="fit-content"
          backgroundColor="#E2E8F0"
          boxShadow="md"
          borderRadius="md"
        >
          <EmojiPicker
            theme={Theme.AUTO}
            lazyLoadEmojis
            suggestedEmojisMode={SuggestionMode.RECENT}
            previewConfig={{ showPreview: false }}
            onEmojiClick={(emojiObject) => {
              const editor = trixEditorElementRef.current?.editor;
              if (!editor) return;
              editor.element?.focus();
              editor.insertString(emojiObject.emoji);
              emojiPicker.onClose();
            }}
          />
        </Box>
      )}
    </>
  );
}

export default TrixEditor;
